'use client';
import React, { useState, useMemo } from 'react';
import { BellAlertIcon, CheckCircleIcon, UserGroupIcon, FunnelIcon, MagnifyingGlassIcon, XMarkIcon, ChevronDownIcon, ChevronUpIcon,  } from '@heroicons/react/24/outline';
import { useAlerts, SentAlert, AlertType, ALERT_TYPE_COLORS, ALERT_TYPE_DESCRIPTIONS, ALERT_TYPES } from '@/context/AlertContext';

function formatTs(iso: string) {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function timeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Hace un momento';
  if (mins < 60) return `Hace ${mins} min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `Hace ${hrs}h`;
  return `Hace ${Math.floor(hrs / 24)}d`;
}

function AlertTypeBadge({ type }: { type: AlertType }) {
  const color = ALERT_TYPE_COLORS[type];
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border whitespace-nowrap"
      style={{ color, borderColor: color + '55', background: color + '18' }}
    >
      {type}
    </span>
  );
}

function AlertRow({ alert }: { alert: SentAlert }) {
  const [expanded, setExpanded] = useState(false);
  const ackCount = alert.recipients.filter(r => r.acknowledged).length;
  const total = alert.recipients.length;
  const allAck = ackCount === total && total > 0;

  return (
    <div className="border border-border rounded-xl overflow-hidden bg-card/60 hover:bg-card/80 transition-colors">
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full flex items-start gap-4 p-4 text-left"
      >
        {/* Icon */}
        <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
          allAck ? 'bg-emerald-500/15 border border-emerald-500/30' : 'bg-orange-500/15 border border-orange-500/30'
        }`}>
          <BellAlertIcon className={`w-4.5 h-4.5 ${allAck ? 'text-emerald-400' : 'text-orange-400'}`} style={{ width: 18, height: 18 }} />
        </div>

        {/* Main info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-sm font-semibold text-foreground truncate">{alert.name}</span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
              alert.kind === 'area' ? 'bg-purple-500/15 text-purple-400' : 'bg-blue-500/15 text-blue-400'
            }`}>
              {alert.kind === 'area' ? 'Área' : 'Notificación'}
            </span>
          </div>
          <div className="flex flex-wrap gap-1 mb-2">
            {alert.types.map(t => <AlertTypeBadge key={t} type={t} />)}
          </div>
          {alert.message && (
            <p className="text-xs text-muted-foreground line-clamp-1">{alert.message}</p>
          )}
        </div>

        {/* Right side */}
        <div className="flex flex-col items-end gap-1.5 shrink-0">
          <span className="text-[11px] text-muted-foreground">{timeAgo(alert.sentAt)}</span>
          <div className="flex items-center gap-1.5">
            <UserGroupIcon className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">{total}</span>
            {allAck
              ? <CheckCircleIcon className="w-3.5 h-3.5 text-emerald-400" />
              : <span className="text-[10px] text-orange-400 font-medium">{ackCount}/{total} ACK</span>
            }
          </div>
          {expanded
            ? <ChevronUpIcon className="w-3.5 h-3.5 text-muted-foreground" />
            : <ChevronDownIcon className="w-3.5 h-3.5 text-muted-foreground" />
          }
        </div>
      </button>

      {/* Expanded detail */}
      {expanded && (
        <div className="border-t border-border px-4 pb-4 pt-3 space-y-3">
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div>
              <span className="text-muted-foreground">Enviado</span>
              <p className="text-foreground font-mono mt-0.5">{formatTs(alert.sentAt)}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Tipo de alerta</span>
              <p className="text-foreground mt-0.5">{alert.kind === 'area' ? `Área (${alert.areaShape ?? 'polígono'})` : 'Notificación directa'}</p>
            </div>
            {alert.zone && (
              <div>
                <span className="text-muted-foreground">Zona</span>
                <p className="text-foreground mt-0.5">{alert.zone}</p>
              </div>
            )}
            {alert.message && (
              <div className="col-span-2">
                <span className="text-muted-foreground">Mensaje</span>
                <p className="text-foreground mt-0.5 bg-muted/30 rounded-lg px-3 py-2">{alert.message}</p>
              </div>
            )}
          </div>

          {/* Types detail */}
          <div>
            <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">Tipos de alerta</p>
            <div className="flex flex-col gap-1">
              {alert.types.map(t => (
                <div key={t} className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ background: ALERT_TYPE_COLORS[t] }} />
                  <span className="text-xs font-medium" style={{ color: ALERT_TYPE_COLORS[t] }}>{t}</span>
                  <span className="text-xs text-muted-foreground">— {ALERT_TYPE_DESCRIPTIONS[t]}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Recipients */}
          <div>
            <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">
              Destinatarios ({total})
            </p>
            <div className="grid grid-cols-2 gap-1.5">
              {alert.recipients.map(r => (
                <div
                  key={r.dotId}
                  className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border text-xs ${
                    r.acknowledged
                      ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-300' :'bg-muted/30 border-border text-muted-foreground'
                  }`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${r.acknowledged ? 'bg-emerald-400' : 'bg-orange-400'}`} />
                  <span className="font-mono font-semibold">#{r.dotId}</span>
                  <span className="truncate">{r.name}</span>
                  {r.acknowledged && r.acknowledgedAt && (
                    <span className="ml-auto text-[9px] text-emerald-500/70 shrink-0">{timeAgo(r.acknowledgedAt)}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AlertasScreen() {
  const { sentAlerts } = useAlerts();
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState<AlertType | 'all'>('all');
  const [filterKind, setFilterKind] = useState<'all' | 'notification' | 'area'>('all');
  const [showFilters, setShowFilters] = useState(false);

  const filtered = useMemo(() => {
    return sentAlerts.filter(a => {
      if (filterKind !== 'all' && a.kind !== filterKind) return false;
      if (filterType !== 'all' && !a.types.includes(filterType)) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        if (!a.name.toLowerCase().includes(q) && !a.message.toLowerCase().includes(q) &&
            !a.recipients.some(r => r.dotId.includes(q) || r.name.toLowerCase().includes(q))) return false;
      }
      return true;
    });
  }, [sentAlerts, search, filterType, filterKind]);

  const totalRecipients = sentAlerts.reduce((s, a) => s + a.recipients.length, 0);
  const totalAck = sentAlerts.reduce((s, a) => s + a.recipients.filter(r => r.acknowledged).length, 0);

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-card/50 shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-orange-500/15 border border-orange-500/30 flex items-center justify-center">
            <BellAlertIcon className="w-5 h-5 text-orange-400" />
          </div>
          <div>
            <h1 className="text-base font-semibold text-foreground">Historial de Alertas</h1>
            <p className="text-xs text-muted-foreground">{sentAlerts.length} alertas enviadas · {totalAck}/{totalRecipients} confirmaciones</p>
          </div>
        </div>
      </div>

      {/* Stats bar */}
      <div className="grid grid-cols-4 gap-px bg-border shrink-0">
        {[
          { label: 'Total alertas', value: sentAlerts.length, color: 'text-foreground' },
          { label: 'Notificación', value: sentAlerts.filter(a => a.kind === 'notification').length, color: 'text-blue-400' },
          { label: 'Área', value: sentAlerts.filter(a => a.kind === 'area').length, color: 'text-purple-400' },
          { label: 'Confirmadas', value: `${totalAck}/${totalRecipients}`, color: 'text-emerald-400' },
        ].map(s => (
          <div key={s.label} className="bg-card/60 px-4 py-3 text-center">
            <p className={`text-lg font-bold ${s.color}`}>{s.value}</p>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border shrink-0">
        <div className="flex items-center gap-2 bg-card border border-border rounded-lg px-3 py-2 flex-1 max-w-xs">
          <MagnifyingGlassIcon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
          <input
            type="text"
            placeholder="Buscar alertas…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none w-full"
          />
          {search && (
            <button onClick={() => setSearch('')} className="text-muted-foreground hover:text-foreground">
              <XMarkIcon className="w-3 h-3" />
            </button>
          )}
        </div>

        <div className="relative">
          <button
            onClick={() => setShowFilters(p => !p)}
            className={`flex items-center gap-2 border rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
              filterType !== 'all' || filterKind !== 'all' ?'border-primary text-primary bg-primary/10' :'border-border text-muted-foreground hover:text-foreground bg-card'
            }`}
          >
            <FunnelIcon className="w-3.5 h-3.5" />
            Filtrar
            <ChevronDownIcon className="w-3 h-3" />
          </button>
          {showFilters && (
            <div className="absolute top-full left-0 mt-1 bg-card border border-border rounded-xl shadow-2xl p-3 w-64 z-50" onClick={e => e.stopPropagation()}>
              <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">Tipo de envío</p>
              <div className="flex gap-1.5 mb-3">
                {(['all', 'notification', 'area'] as const).map(k => (
                  <button
                    key={k}
                    onClick={() => setFilterKind(k)}
                    className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      filterKind === k ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {k === 'all' ? 'Todos' : k === 'notification' ? 'Notif.' : 'Área'}
                  </button>
                ))}
              </div>
              <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">Tipo de alerta</p>
              <div className="flex flex-col gap-1 max-h-48 overflow-y-auto">
                <button
                  onClick={() => setFilterType('all')}
                  className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-xs transition-colors ${filterType === 'all' ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted'}`}
                >
                  Todos los tipos
                </button>
                {ALERT_TYPES.map(t => (
                  <button
                    key={t}
                    onClick={() => setFilterType(t)}
                    className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-xs transition-colors ${filterType === t ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted'}`}
                  >
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ background: ALERT_TYPE_COLORS[t] }} />
                    {t}
                  </button>
                ))}
              </div>
              {(filterType !== 'all' || filterKind !== 'all') && (
                <button
                  onClick={() => { setFilterType('all'); setFilterKind('all'); }}
                  className="mt-2 w-full text-xs text-muted-foreground hover:text-foreground border border-border rounded-lg py-1.5 transition-colors"
                >
                  Limpiar filtros
                </button>
              )}
            </div>
          )}
        </div>

        <span className="text-xs text-muted-foreground ml-auto">
          {filtered.length} resultado{filtered.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 gap-4">
            <div className="w-16 h-16 rounded-2xl bg-muted/30 flex items-center justify-center">
              <BellAlertIcon className="w-8 h-8 text-muted-foreground/40" />
            </div>
            <div className="text-center">
              <p className="text-sm font-medium text-muted-foreground">
                {sentAlerts.length === 0 ? 'No hay alertas enviadas' : 'Sin resultados'}
              </p>
              <p className="text-xs text-muted-foreground/60 mt-1">
                {sentAlerts.length === 0
                  ? 'Las alertas enviadas desde el mapa aparecerán aquí' :'Intenta con otros filtros de búsqueda'
                }
              </p>
            </div>
          </div>
        ) : (
          filtered.map(alert => <AlertRow key={alert.id} alert={alert} />)
        )}
      </div>
    </div>
  );
}
